import { getSMSData, resendSMSData } from 'api/endpoints/farmers';
import useApi from 'hooks/useApi';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Modal, Button, Spinner, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { FiDownload } from 'react-icons/fi';
import { formSubmissionToast } from 'helpers/utils';
import { toast } from 'react-toastify';

const DeliveryDetailsModal = ({
  show,
  onClose,
  selectedModalData,
  refreshTable
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState({
    show: false,
    statusId: null
  });
  const { data: excelData, request } = useApi(getSMSData);
  const tenant = '03534add672340809e2c70314b1ce20e';

  useEffect(() => {
    const fetchData = async () => {
      if (selectedModalData?.extra?.request_id) {
        const id = selectedModalData.extra.request_id;

        setLoading(true);
        await request({ tenant, id });
        setData(selectedModalData?.extra?.data);
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedModalData?.extra?.request_id]);

  useEffect(() => {
    if (excelData) {
      const tableData = get(excelData, 'results.data', []);
      setExportData(tableData);
    }
  }, [excelData]);

  const handleDownloadExcel = () => {
    if (!exportData || exportData.length === 0) return;

    const title = 'Campaign Status Report';
    const publishDate = exportData[0].publish_date;
    const smsContent = exportData[0].sms_content;
    const excelData = [
      [title],
      [`Published Date: ${publishDate}`, `Message Content: ${smsContent}`],
      [],
      [
        'Farmer Name',
        'Mobile Number',
        'Farmer Village',
        'Farmer Group',
        'SMS Status',
        'Delivery Status'
      ],
      ...exportData.map(item => [
        item.name,
        item.phone_number,
        item.farmer_village,
        item.farmer_group,
        item?.['SMS Status'],
        item.delivery_status
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
    ws['A1'].s = {
      alignment: { horizontal: 'center', vertical: 'center' },
      font: { bold: true, sz: 14 }
    };

    ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campaign Status Report');
    XLSX.writeFile(wb, 'Campaign_Status_Report.xlsx');
  };

  const handleResend = async id => {
    // First close the confirmation modal for better UX
    setShowConfirmation({ show: false, statusId: null });

    if (id) {
      // Call the resend function
      const requestID = selectedModalData?.extra?.request_id;
      try {
        setLoading(true);
        const res = await resendSMSData({ requestID, id });
        formSubmissionToast(res, toast, 'SMS resent successfully');

        // Refresh the modal's data after resending
        if (requestID) {
          await request({ tenant, id: requestID });
        }

        // Create a function to refresh and close the modal
        const refreshAndClose = async () => {
          try {
            // Refresh the parent table data using direct API call
            if (typeof refreshTable === 'function') {
              console.log(
                'Calling direct API refresh function (Promise-based)'
              );
              try {
                await refreshTable(); // Wait for the refresh to complete
                console.log('Table refresh completed successfully');
              } catch (refreshErr) {
                console.error('Error in refreshTable function:', refreshErr);
              }
            } else if (window.refreshDynamicReportTable) {
              // Fallback to global direct API refresh function
              console.log(
                'Using global refreshDynamicReportTable direct API function'
              );
              try {
                await window.refreshDynamicReportTable();
              } catch (globalRefreshErr) {
                console.error(
                  'Error in global refresh function:',
                  globalRefreshErr
                );
              }
            } else if (window.refreshTableData) {
              // Try Table.jsx refresh function as another fallback
              console.log('Using global refreshTableData direct API function');
              try {
                await window.refreshTableData();
              } catch (tableRefreshErr) {
                console.error(
                  'Error in Table refresh function:',
                  tableRefreshErr
                );
              }
            } else {
              // If no refresh function is available, close the modal with refresh flag
              console.log(
                'No refresh function available, closing modal with refresh flag'
              );
            }
          } catch (err) {
            console.error('Error during table refresh:', err);
          } finally {
            // Always close the modal after refresh attempt (success or failure)
            setLoading(false);
            handleCloseModal(true);
          }
        };

        // Execute the refresh and close function
        await refreshAndClose();
      } catch (err) {
        setLoading(false);
        formSubmissionToast(err, toast, 'Error sending SMS');
        console.error('Error resending SMS:', err);
      }
    }
  };

  const handleCloseModal = (refreshNeeded = false) => {
    // When closing the main modal, call onClose with the refresh flag
    console.log('Closing modal with refreshNeeded:', refreshNeeded);
    onClose(refreshNeeded);
  };

  return (
    <Modal
      show={show}
      onHide={() => handleCloseModal(false)}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Campaign Status Report</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="modal-body overflow-auto"
        style={{ maxHeight: '400px' }}
      >
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Published Date:</strong>{' '}
              <span>{selectedModalData?.extra?.published_date}</span>
            </div>
            <Button variant="secondary" onClick={handleDownloadExcel}>
              <FiDownload />
            </Button>
          </div>
          <div>
            <strong>Message Content:</strong>{' '}
            {selectedModalData?.extra?.message_content}
          </div>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Farmers Count</th>
                <th>SMS Delivery Status</th>
                <th>Status Description</th>
                <th>SMS Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((details, index) => (
                <tr key={index}>
                  <td>{details?.count}</td>
                  <td>{details?.delivery_status}</td>
                  <td>{details?.description}</td>
                  <td>{details?.['SMS Status'] ?? details?.sms_status}</td>
                  <td>
                    {details?.action?.resend && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          setShowConfirmation({
                            show: true,
                            statusId: details?.status_id
                          })
                        }
                      >
                        Resend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <Modal
          show={showConfirmation.show}
          onHide={() => setShowConfirmation({ show: false, statusId: null })}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirmation</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to resend the SMS?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() =>
                setShowConfirmation({ show: false, statusId: null })
              }
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleResend(showConfirmation?.statusId)}
            >
              Ok
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>
    </Modal>
  );
};

DeliveryDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedModalData: PropTypes.shape({
    extra: PropTypes.shape({
      request_id: PropTypes.string,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          delivery_status: PropTypes.string,
          description: PropTypes.string
        })
      ),
      published_date: PropTypes.string,
      message_content: PropTypes.string
    })
  }),
  refreshTable: PropTypes.func
};

DeliveryDetailsModal.defaultProps = {
  selectedModalData: {
    extra: {}
  }
};

export default DeliveryDetailsModal;
